import React, { useState, useEffect } from 'react';
import './room.css';

const API_URL = process.env.REACT_APP_API_URL || 'https://app.learninghub.ind.in';

const AdminRoomPage = ({ onNavigate, userInfo }) => {
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [roomCategoryFilter, setRoomCategoryFilter] = useState('all');
  const [floorFilter, setFloorFilter] = useState('all');
  const [availabilityFilter, setAvailabilityFilter] = useState('all');
  const [notificationOpen, setNotificationOpen] = useState(false);
  const [selectedRoom, setSelectedRoom] = useState(null);
  const [assignModalOpen, setAssignModalOpen] = useState(false);
  const [selectedBed, setSelectedBed] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [loading, setLoading] = useState(true);
  const [roomsData, setRoomsData] = useState([]);
  const [availableStudents, setAvailableStudents] = useState([]);
  const [dashboardData, setDashboardData] = useState(null);
  const [error, setError] = useState(null);
  const [roomStats, setRoomStats] = useState(null);
  const [assignLoading, setAssignLoading] = useState(false);
  const [studentDetailsModal, setStudentDetailsModal] = useState(false);
  const [selectedStudent, setSelectedStudent] = useState(null);

  // User privileges state
  const [userPrivileges, setUserPrivileges] = useState([]);
  const [privilegesLoading, setPrivilegesLoading] = useState(true);

  useEffect(() => {
    if (userInfo?.userId) {
      fetchDashboardData();
      fetchUserPrivileges();
      fetchRooms();
      fetchRoomStats();
    }
  }, [userInfo]);

  useEffect(() => {
    if (userInfo?.userId) {
      const timer = setTimeout(() => {
        fetchRooms();
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [roomCategoryFilter, floorFilter, searchQuery]);

  const fetchDashboardData = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/dashboard/${userInfo.userId}`);
      const data = await response.json();
      if (data.success) {
        setDashboardData(data);
      }
    } catch (err) {
      console.error('Error fetching dashboard:', err);
    }
  }

  // Fetch user privileges
  const fetchUserPrivileges = async () => {
    try {
      setPrivilegesLoading(true);
      const response = await fetch(`${API_URL}/admin/user-privileges/${userInfo.userId}`);
      const data = await response.json();
      
      if (data.success && data.privileges) {
        setUserPrivileges(data.privileges);
      } else {
        setUserPrivileges([]);
      }
    } catch (err) {
      console.error('Error fetching privileges:', err);
      setUserPrivileges([]);
    } finally {
      setPrivilegesLoading(false);
    }
  };

  // Group privileges by category
  const getPrivilegesByCategory = (category) => {
    return userPrivileges.filter(privilege => privilege.page_category === category);
  };
;

  const fetchRooms = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const queryParams = new URLSearchParams();
      if (roomCategoryFilter !== 'all') queryParams.append('roomType', roomCategoryFilter);
      if (floorFilter !== 'all') queryParams.append('floorNumber', floorFilter);
      if (searchQuery) queryParams.append('search', searchQuery);
      
      const response = await fetch(`${API_URL}/admin/rooms/${userInfo.userId}?${queryParams}`);
      const data = await response.json();
      
      if (data.success) {
        setRoomsData(data.rooms || []);
      } else {
        setError(data.message || 'Failed to load rooms');
      }
    } catch (err) {
      console.error('Error fetching rooms:', err);
      setError('Failed to connect to server');
    } finally {
      setLoading(false);
    }
  };

  const fetchRoomStats = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/room-stats/${userInfo.userId}`);
      const data = await response.json();
      if (data.success) {
        setRoomStats(data.statistics);
      }
    } catch (err) {
      console.error('Error fetching room stats:', err);
    }
  };

  const fetchAvailableStudents = async () => {
    try {
      const response = await fetch(`${API_URL}/admin/available-students/${userInfo.userId}`);
      const data = await response.json();
      if (data.success) {
        setAvailableStudents(data.students || []);
      }
    } catch (err) {
      console.error('Error fetching available students:', err);
    }
  };

  const handleAssignBed = async (student) => {
    if (!selectedRoom || selectedBed === null) return;
    
    setAssignLoading(true);
    try {
      const response = await fetch(`${API_URL}/admin/assign-room/${userInfo.userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          studentPhone: student.phone_number,
          roomNumber: selectedRoom.roomNumber,
          bedNumber: selectedBed,
          blockName: selectedRoom.blockName,
          floorNumber: selectedRoom.floorNumber,
          roomType: selectedRoom.roomType
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        setAssignModalOpen(false);
        setSelectedBed(null);
        await fetchRooms();
        await fetchRoomStats();
        alert('Student assigned successfully!');
      } else {
        alert(data.message || 'Failed to assign student');
      }
    } catch (err) {
      console.error('Error assigning student:', err);
      alert('Failed to assign student');
    } finally {
      setAssignLoading(false);
    }
  };

  const handleRemoveStudent = async (roomNumber, bedNumber, studentUserId) => {
    if (!window.confirm('Are you sure you want to remove this student from the room?')) {
      return;
    }
    
    try {
      const response = await fetch(`${API_URL}/admin/remove-from-room/${userInfo.userId}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          studentUserId,
          roomNumber,
          bedNumber
        })
      });
      
      const data = await response.json();
      
      if (data.success) {
        await fetchRooms();
        await fetchRoomStats();
        alert('Student removed successfully!');
      } else {
        alert(data.message || 'Failed to remove student');
      }
    } catch (err) {
      console.error('Error removing student:', err);
      alert('Failed to remove student');
    }
  };

  const openAssignModal = (room, bedNumber) => {
    setSelectedRoom(room);
    setSelectedBed(bedNumber);
    setAssignModalOpen(true);
    fetchAvailableStudents();
  };

  const viewStudentDetails = (student, room, bedNumber) => {
    setSelectedStudent({
      ...student,
      roomNumber: room.roomNumber,
      bedNumber: bedNumber,
      floorNumber: room.floorNumber,
      roomType: room.roomCategory,
      isAC: room.isAC,
      rentAmount: room.rentAmount
    });
    setStudentDetailsModal(true);
  };

  const notifications = [
    {
      id: 1,
      title: 'New Student Registration',
      message: 'Anjali Patel has registered for room 45',
      time: '5 minutes ago',
      icon: '👤',
      type: 'success',
      read: false
    },
    {
      id: 2,
      title: 'Payment Received',
      message: 'Rohan Kumar paid ₹6000 for room 12',
      time: '30 minutes ago',
      icon: '💰',
      type: 'info',
      read: false
    }
  ];

  const getAvailability = (room) => {
    const occupiedBeds = room.occupiedBeds || 0;
    if (occupiedBeds === 0) return 'Available';
    if (occupiedBeds === room.totalBeds) return 'Not Available';
    return 'Partially Available';
  };

  const getAvailabilityColor = (availability) => {
    switch (availability) {
      case 'Available': return '#52ab98';
      case 'Not Available': return '#dc2626';
      case 'Partially Available': return '#f59e0b';
      default: return '#6b7280';
    }
  };

  const MenuItem = ({ icon, label, active = false, route }) => (
    <div 
      className={`menu-item ${active ? 'menu-item-active' : ''}`}
      onClick={() => {
        if (route && onNavigate) {
          onNavigate(route);
        }
        setSidebarOpen(false);
      }}
    >
      <span className="menu-icon">{icon}</span>
      <span className="menu-label">{label}</span>
      <div className="menu-glow"></div>
    </div>
  );

  const handleLogout = () => {
    sessionStorage.clear();
    if (onNavigate) {
      onNavigate('hostel-login');
    }
  };

  const filteredRooms = roomsData.filter(room => {
    const availability = getAvailability(room);
    const availabilityMatch = availabilityFilter === 'all' || 
      availability.toLowerCase().replace(' ', '-') === availabilityFilter.toLowerCase();
    return availabilityMatch;
  });

  if (loading && roomsData.length === 0) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: '100vh',
        fontFamily: 'Inter, sans-serif',
        fontSize: '18px',
        color: '#2b6777'
      }}>
        Loading rooms...
      </div>
    );
  }

  return (
    <>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800;900&display=swap');

        * {
          margin: 0;
          padding: 0;
          box-sizing: border-box;
        }

        body {
          font-family: 'Inter', -apple-system, BlinkMacSystemFont, sans-serif;
          -webkit-font-smoothing: antialiased;
          overflow-x: hidden;
        }

        @keyframes shimmer {
          0% { background-position: -1000px 0; }
          100% { background-position: 1000px 0; }
        }

        @keyframes pulse {
          0%, 100% { transform: scale(1); }
          50% { transform: scale(1.05); }
        }

        @keyframes glow {
          0%, 100% { opacity: 0.5; }
          50% { opacity: 1; }
        }

        @keyframes slideIn {
          from { opacity: 0; transform: translateY(30px); }
          to { opacity: 1; transform: translateY(0); }
        }

        @keyframes fadeIn {
          from { opacity: 0; }
          to { opacity: 1; }
        }

        .dashboard-container {
          display: flex;
          height: 100vh;
          background: #ffffff;
          overflow: hidden;
        }

        .sidebar {
          position: fixed;
          top: 0;
          left: 0;
          bottom: 0;
          width: 280px;
          background: #f5f7f6;
          transform: translateX(-100%);
          transition: transform 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          z-index: 100;
          display: flex;
          flex-direction: column;
          box-shadow: 8px 0 40px rgba(43,103,119,0.15);
        }

        .sidebar.open {
          transform: translateX(0);
        }

        .sidebar-header {
          padding: 24px 20px;
          background: linear-gradient(135deg, rgba(199,229,225,0.3) 0%, rgba(242,248,247,0.5) 100%);
          border-bottom: 1px solid rgba(82,171,152,0.15);
        }

        .sidebar-top {
          display: flex;
          justify-content: flex-end;
          margin-bottom: 16px;
        }

        .close-btn {
          background: rgba(43,103,119,0.1);
          border: none;
          cursor: pointer;
          font-size: 18px;
          color: #2b6777;
          width: 32px;
          height: 32px;
          border-radius: 8px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .close-btn:hover {
          background: rgba(43,103,119,0.2);
          transform: rotate(90deg);
        }

        .user-info-sidebar {
          display: flex;
          align-items: center;
          gap: 12px;
          position: relative;
          z-index: 1;
        }

        .user-avatar-sidebar {
          width: 50px;
          height: 50px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 50%;
          display: flex;
          align-items: center;
          justify-content: center;
          color: white;
          font-weight: 800;
          font-size: 20px;
          box-shadow: 0 4px 12px rgba(43,103,119,0.2);
          flex-shrink: 0;
        }

        .user-details-sidebar {
          flex: 1;
        }

        .user-name-sidebar {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .user-role-sidebar {
          font-size: 13px;
          color: #52ab98;
          font-weight: 600;
        }

        .sidebar-nav {
          flex: 1;
          overflow-y: auto;
          padding: 24px 16px;
          background: #f5f7f6;
        }

        .sidebar-nav::-webkit-scrollbar {
          width: 6px;
        }

        .sidebar-nav::-webkit-scrollbar-track {
          background: transparent;
        }

        .sidebar-nav::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 3px;
        }

        .nav-section {
          margin-bottom: 28px;
        }

        .nav-label {
          font-size: 11px;
          font-weight: 700;
          color: rgba(43,103,119,0.45);
          text-transform: uppercase;
          letter-spacing: 1.2px;
          margin-bottom: 12px;
          padding: 0 12px;
          padding-left: 18px;
        }

        .menu-item {
          position: relative;
          display: flex;
          align-items: center;
          gap: 14px;
          padding: 16px 18px;
          margin-bottom: 10px;
          cursor: pointer;
          color: #2b6777;
          font-size: 15px;
          font-weight: 600;
          border-radius: 14px;
          transition: all 0.3s;
          overflow: hidden;
        }

        .menu-glow {
          display: none;}

        .menu-item:hover {
          color: #52ab98;
          background: rgba(82,171,152,0.1);
          transform: translateX(6px);
          box-shadow: 0 4px 16px rgba(82,171,152,0.15);
        }

        .menu-item:hover .menu-glow {
          display: none;}

        .menu-item-active {
          background: linear-gradient(135deg, rgba(43,103,119,0.15) 0%, rgba(82,171,152,0.15) 100%);
          color: #2b6777;
          box-shadow: 0 6px 20px rgba(82,171,152,0.2);
          font-weight: 700;
        }

        .menu-item-active .menu-glow {
          display: none;}

        .menu-icon {
          font-size: 22px;
          position: relative;
          z-index: 1;
        }

        .menu-label {
          position: relative;
          z-index: 1;
        }

        .logout-section {
          padding: 16px;
          background: #f5f7f6;
        }

        .logout-btn {
          width: 100%;
          padding: 16px 20px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
          border: none;
          border-radius: 14px;
          font-size: 15px;
          font-weight: 700;
          cursor: pointer;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 10px;
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
        }

        .logout-btn:hover {
          transform: translateY(-3px);
          box-shadow: 0 10px 35px rgba(43,103,119,0.4);
        }

        .overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.4);
          backdrop-filter: blur(6px);
          z-index: 90;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s ease;
        }

        .overlay.show {
          opacity: 1;
          visibility: visible;
        }


        .no-privileges-message {
          padding: 40px 20px;
          text-align: center;
          background: rgba(255,249,196,0.3);
          border-radius: 12px;
          margin: 20px;
        }

        .no-privileges-icon {
          font-size: 48px;
          margin-bottom: 12px;
        }

        .no-privileges-text {
          font-size: 16px;
          font-weight: 700;
          color: #2b6777;
          margin-bottom: 6px;
        }

        .no-privileges-subtext {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
        }

        .main-content {
          flex: 1;
          display: flex;
          flex-direction: column;
          overflow: hidden;
          width: 100%;
        }

        .top-header {
          background: #ffffff;
          padding: 52px 32px;
          display: flex;
          align-items: center;
          justify-content: space-between;
          border-bottom: 1px solid rgba(82,171,152,0.12);
          position: relative;
          box-shadow: 0 2px 8px rgba(43,103,119,0.06);
        }

        .top-header::after {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 3px;
          background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
        }

        .header-left {
          display: flex;
          align-items: center;
          gap: 24px;
        }

        .menu-toggle {
          background: linear-gradient(135deg, rgba(43,103,119,0.1) 0%, rgba(82,171,152,0.1) 100%);
          border: 2px solid rgba(82,171,152,0.25);
          cursor: pointer;
          font-size: 26px;
          color: #2b6777;
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          box-shadow: 0 4px 20px rgba(43,103,119,0.15);
        }

        .menu-toggle:hover {
          transform: scale(1.05);
          box-shadow: 0 6px 30px rgba(43,103,119,0.25);
        }

        .header-title-section {
          display: flex;
          flex-direction: column;
          gap: 4px;
        }

        .header-greeting {
          font-size: 18px;
          color: #52ab98;
          font-weight: 600;
        }

        .header-name {
          font-size: 28px;
          font-weight: 900;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .header-right {
          display: flex;
          align-items: center;
          gap: 14px;
        }

        .notification-btn {
          position: relative;
          background: rgba(82,171,152,0.1);
          border: 2px solid rgba(82,171,152,0.2);
          cursor: pointer;
          font-size: 24px;
          color: #2b6777;
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
        }

        .notification-btn:hover,
        .notification-btn.active {
          background: rgba(82,171,152,0.2);
          border-color: #52ab98;
          box-shadow: 0 6px 24px rgba(82,171,152,0.3);
          transform: scale(1.05);
        }

        .notification-badge {
          position: absolute;
          top: 6px;
          right: 6px;
          min-width: 22px;
          height: 22px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 11px;
          font-size: 11px;
          font-weight: 800;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 0 6px;
          color: white;
          border: 2px solid white;
          box-shadow: 0 3px 12px rgba(43,103,119,0.4);
          animation: pulse 2s infinite;
        }

        .user-avatar-header {
          width: 52px;
          height: 52px;
          border-radius: 50%;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          display: flex;
          align-items: center;
          justify-content: center;
          font-weight: 800;
          font-size: 20px;
          color: white;
          cursor: pointer;
          border: 3px solid rgba(82,171,152,0.3);
          transition: all 0.3s;
          box-shadow: 0 6px 24px rgba(43,103,119,0.3);
        }

        .user-avatar-header:hover {
          transform: scale(1.1);
          box-shadow: 0 8px 32px rgba(43,103,119,0.5);
        }

        .notifications-panel {
          position: fixed;
          top: 100px;
          right: 32px;
          width: 440px;
          max-width: calc(100vw - 64px);
          max-height: 640px;
          background: rgba(255,255,255,0.98);
          backdrop-filter: blur(40px);
          border-radius: 24px;
          box-shadow: 0 24px 70px rgba(43,103,119,0.25);
          z-index: 1000;
          opacity: 0;
          visibility: hidden;
          transform: translateY(-20px);
          transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
          border: 2px solid rgba(82,171,152,0.2);
          overflow: hidden;
        }

        .notifications-panel.open {
          opacity: 1;
          visibility: visible;
          transform: translateY(0);
        }

        .notifications-header {
          padding: 28px 28px 24px;
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
          position: relative;
          z-index: 1;
        }

        .notifications-title {
          font-size: 20px;
          font-weight: 900;
          color: #2b6777;
        }

        .close-notification-btn {
          background: rgba(82,171,152,0.1);
          border: 1.5px solid rgba(82,171,152,0.25);
          color: #2b6777;
          cursor: pointer;
          width: 32px;
          height: 32px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          font-size: 16px;
        }

        .close-notification-btn:hover {
          background: rgba(82,171,152,0.2);
          border-color: #52ab98;
        }

        .notifications-list {
          max-height: 480px;
          overflow-y: auto;
          position: relative;
          z-index: 1;
        }

        .notification-item {
          padding: 24px 28px;
          border-bottom: 1px solid rgba(82,171,152,0.1);
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          gap: 18px;
          align-items: flex-start;
          position: relative;
        }

        .notification-item::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: transparent;
          transition: all 0.3s;
        }

        .notification-item:hover {
          background: rgba(82,171,152,0.08);
        }

        .notification-item:hover::before {
          background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
        }

        .notification-item.unread {
          background: rgba(82,171,152,0.05);
        }

        .notification-icon-wrapper {
          width: 52px;
          height: 52px;
          border-radius: 14px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 26px;
          flex-shrink: 0;
          background: rgba(82,171,152,0.1);
          border: 1.5px solid rgba(82,171,152,0.2);
        }

        .notification-content {
          flex: 1;
        }

        .notification-header-item {
          display: flex;
          align-items: center;
          gap: 10px;
          margin-bottom: 8px;
        }

        .notification-title {
          font-size: 15px;
          font-weight: 800;
          color: #2b6777;
          flex: 1;
        }

        .notification-unread-dot {
          width: 10px;
          height: 10px;
          background: #52ab98;
          border-radius: 50%;
          box-shadow: 0 0 12px #52ab98;
        }

        .notification-message {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          margin-bottom: 8px;
          line-height: 1.6;
        }

        .notification-time {
          font-size: 12px;
          color: rgba(43,103,119,0.5);
          font-weight: 700;
        }

        .notification-overlay {
          position: fixed;
          inset: 0;
          z-index: 999;
          opacity: 0;
          visibility: hidden;
          transition: all 0.3s;
        }

        .notification-overlay.show {
          opacity: 1;
          visibility: visible;
        }

        .content-area {
          flex: 1;
          overflow-y: auto;
          background: #ffffff;
          padding: 32px;
        }

        .content-area::-webkit-scrollbar {
          width: 8px;
        }

        .content-area::-webkit-scrollbar-track {
          background: rgba(82,171,152,0.08);
        }

        .content-area::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 4px;
        }

        .page-header {
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          padding: 40px 32px;
          margin-bottom: 32px;
          border-radius: 24px;
          position: relative;
          overflow: hidden;
          border: 1px solid rgba(82,171,152,0.15);
        }

        .page-header::before {
          content: '';
          position: absolute;
          bottom: 0;
          left: 0;
          right: 0;
          height: 4px;
          background: linear-gradient(90deg, #2b6777 0%, #52ab98 100%);
        }

        .page-title {
          font-size: 36px;
          font-weight: 900;
          color: #2b6777;
          margin-bottom: 8px;
          letter-spacing: -0.5px;
        }

        .page-subtitle {
          font-size: 16px;
          color: rgba(43,103,119,0.6);
          font-weight: 500;
        }

        .stats-grid {
          display: grid;
          grid-template-columns: repeat(auto-fit, minmax(250px, 1fr));
          gap: 20px;
          margin-bottom: 32px;
          animation: slideIn 0.6s ease-out;
        }

        .stat-card {
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(30px);
          border-radius: 20px;
          padding: 24px;
          border: 2px solid rgba(82,171,152,0.2);
          box-shadow: 0 4px 16px rgba(43,103,119,0.08);
          transition: all 0.3s;
        }

        .stat-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 8px 24px rgba(43,103,119,0.15);
        }

        .stat-header {
          display: flex;
          align-items: center;
          justify-content: space-between;
          margin-bottom: 16px;
        }

        .stat-label {
          font-size: 14px;
          font-weight: 700;
          color: rgba(43,103,119,0.7);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .stat-icon {
          width: 48px;
          height: 48px;
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          border-radius: 12px;
          display: flex;
          align-items: center;
          justify-content: center;
          font-size: 24px;
          color: white;
        }

        .stat-value {
          font-size: 32px;
          font-weight: 900;
          color: #2b6777;
          margin-bottom: 4px;
        }

        .stat-description {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
        }

        .filter-section {
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(30px);
          border-radius: 20px;
          padding: 24px 28px;
          margin-bottom: 28px;
          border: 2px solid rgba(82,171,152,0.2);
          box-shadow: 0 4px 16px rgba(43,103,119,0.08);
          display: flex;
          align-items: center;
          gap: 20px;
          flex-wrap: wrap;
        }

        .search-box {
          position: relative;
          flex: 1;
          min-width: 280px;
        }

        .search-input {
          width: 100%;
          padding: 14px 20px 14px 48px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 14px;
          font-size: 15px;
          font-weight: 500;
          color: #2b6777;
          outline: none;
          transition: all 0.3s;
          background: rgba(82,171,152,0.05);
        }

        .search-input:focus {
          border-color: #52ab98;
          background: white;
          box-shadow: 0 4px 20px rgba(82,171,152,0.15);
        }

        .search-icon {
          position: absolute;
          left: 16px;
          top: 50%;
          transform: translateY(-50%);
          font-size: 18px;
          color: rgba(43,103,119,0.5);
        }

        .filter-group {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .filter-label {
          font-size: 14px;
          font-weight: 700;
          color: rgba(43,103,119,0.7);
        }

        .filter-select {
          padding: 12px 18px;
          border: 2px solid rgba(82,171,152,0.2);
          border-radius: 12px;
          font-size: 14px;
          font-weight: 600;
          color: #2b6777;
          outline: none;
          cursor: pointer;
          background: rgba(82,171,152,0.05);
          transition: all 0.3s;
          min-width: 140px;
        }

        .filter-select:hover {
          border-color: #52ab98;
          background: white;
        }

        .requests-grid {
          display: grid;
          gap: 20px;
          animation: slideIn 0.6s ease-out;
        }

        .request-card {
          background: rgba(255,255,255,0.9);
          backdrop-filter: blur(30px);
          border-radius: 24px;
          border: 2px solid rgba(82,171,152,0.2);
          padding: 28px;
          transition: all 0.3s;
          cursor: pointer;
          box-shadow: 0 4px 16px rgba(43,103,119,0.08);
          position: relative;
          overflow: hidden;
        }

        .request-card::before {
          content: '';
          position: absolute;
          left: 0;
          top: 0;
          bottom: 0;
          width: 4px;
          background: transparent;
          transition: all 0.3s;
        }

        .request-card:hover {
          transform: translateY(-4px);
          box-shadow: 0 12px 40px rgba(43,103,119,0.2);
          border-color: rgba(82,171,152,0.4);
        }

        .request-card:hover::before {
          background: linear-gradient(180deg, #2b6777 0%, #52ab98 100%);
        }

        .request-header-row {
          display: flex;
          align-items: flex-start;
          justify-content: space-between;
          gap: 20px;
          margin-bottom: 20px;
          padding-bottom: 20px;
          border-bottom: 2px solid rgba(82,171,152,0.1);
        }

        .request-left {
          flex: 1;
        }

        .request-id {
          font-size: 12px;
          font-weight: 700;
          color: #52ab98;
          margin-bottom: 8px;
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .request-issue-title {
          font-size: 20px;
          font-weight: 900;
          color: #2b6777;
          margin-bottom: 12px;
          letter-spacing: -0.3px;
        }

        .request-student-info {
          display: flex;
          align-items: center;
          gap: 14px;
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          font-weight: 600;
        }

        .request-right {
          display: flex;
          flex-direction: column;
          align-items: flex-end;
          gap: 10px;
        }

        .priority-badge {
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 800;
          text-transform: uppercase;
          color: white;
          letter-spacing: 0.5px;
        }

        .category-badge {
          display: inline-flex;
          align-items: center;
          gap: 8px;
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 13px;
          font-weight: 700;
          background: rgba(82,171,152,0.1);
          color: #2b6777;
          border: 1.5px solid rgba(82,171,152,0.2);
        }

        .request-description {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          line-height: 1.7;
          margin-bottom: 20px;
        }

        .request-footer {
          display: flex;
          align-items: center;
          justify-content: space-between;
          flex-wrap: wrap;
          gap: 14px;
        }

        .request-time {
          font-size: 13px;
          color: rgba(43,103,119,0.6);
          font-weight: 600;
        }

        .status-badge {
          padding: 8px 16px;
          border-radius: 10px;
          font-size: 12px;
          font-weight: 800;
          text-transform: uppercase;
          color: white;
          letter-spacing: 0.5px;
        }

        .modal-overlay {
          position: fixed;
          inset: 0;
          background: rgba(43,103,119,0.6);
          backdrop-filter: blur(8px);
          z-index: 1000;
          display: flex;
          align-items: center;
          justify-content: center;
          padding: 20px;
          animation: fadeIn 0.3s ease;
        }

        .modal {
          background: white;
          border-radius: 24px;
          max-width: 600px;
          width: 100%;
          max-height: 80vh;
          overflow: hidden;
          border: 2px solid rgba(82,171,152,0.2);
          box-shadow: 0 24px 70px rgba(43,103,119,0.3);
          animation: slideIn 0.4s ease;
        }

        .modal-header {
          padding: 28px 32px;
          background: linear-gradient(135deg, rgba(43,103,119,0.08) 0%, rgba(82,171,152,0.08) 100%);
          border-bottom: 2px solid rgba(82,171,152,0.15);
          display: flex;
          align-items: center;
          justify-content: space-between;
        }

        .modal-title {
          font-size: 24px;
          font-weight: 900;
          color: #2b6777;
        }

        .modal-close {
          background: rgba(82,171,152,0.1);
          border: 1.5px solid rgba(82,171,152,0.25);
          color: #2b6777;
          cursor: pointer;
          width: 36px;
          height: 36px;
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          transition: all 0.3s;
          font-size: 18px;
        }

        .modal-close:hover {
          background: rgba(82,171,152,0.2);
          transform: rotate(90deg);
        }

        .modal-content {
          padding: 28px 32px;
          max-height: calc(80vh - 180px);
          overflow-y: auto;
        }

        .modal-content::-webkit-scrollbar {
          width: 6px;
        }

        .modal-content::-webkit-scrollbar-track {
          background: rgba(82,171,152,0.08);
        }

        .modal-content::-webkit-scrollbar-thumb {
          background: rgba(82,171,152,0.3);
          border-radius: 3px;
        }

        .modal-section {
          margin-bottom: 24px;
        }

        .modal-section-title {
          font-size: 12px;
          font-weight: 800;
          color: rgba(43,103,119,0.6);
          text-transform: uppercase;
          letter-spacing: 1px;
          margin-bottom: 12px;
        }

        .modal-info-grid {
          display: grid;
          grid-template-columns: repeat(2, 1fr);
          gap: 16px;
        }

        .modal-info-item {
          display: flex;
          flex-direction: column;
          gap: 6px;
          padding: 14px;
          background: rgba(82,171,152,0.05);
          border-radius: 12px;
          border: 1.5px solid rgba(82,171,152,0.1);
        }

        .modal-info-label {
          font-size: 12px;
          font-weight: 700;
          color: rgba(43,103,119,0.6);
          text-transform: uppercase;
          letter-spacing: 0.5px;
        }

        .modal-info-value {
          font-size: 15px;
          font-weight: 700;
          color: #2b6777;
        }

        .modal-description {
          padding: 20px;
          background: rgba(82,171,152,0.05);
          border-radius: 14px;
          border: 1.5px solid rgba(82,171,152,0.1);
        }

        .modal-description-title {
          font-size: 16px;
          font-weight: 800;
          color: #2b6777;
          margin-bottom: 12px;
        }

        .modal-description-text {
          font-size: 14px;
          color: rgba(43,103,119,0.7);
          line-height: 1.7;
        }

        .modal-footer {
          padding: 24px 32px;
          border-top: 2px solid rgba(82,171,152,0.15);
          display: flex;
          gap: 12px;
          flex-wrap: wrap;
        }

        .action-btn {
          flex: 1;
          min-width: 140px;
          padding: 14px 20px;
          border: none;
          border-radius: 12px;
          font-size: 14px;
          font-weight: 700;
          cursor: pointer;
          transition: all 0.3s;
          display: flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
        }

        .action-btn:disabled {
          opacity: 0.5;
          cursor: not-allowed;
        }

        .btn-accept {
          background: linear-gradient(135deg, #2b6777 0%, #52ab98 100%);
          color: white;
        }

        .btn-accept:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(43,103,119,0.3);
        }

        .btn-reject {
          background: rgba(239,68,68,0.1);
          color: #dc2626;
          border: 2px solid rgba(239,68,68,0.2);
        }

        .btn-reject:hover:not(:disabled) {
          background: #dc2626;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(239,68,68,0.3);
        }

        .btn-complete {
          background: linear-gradient(135deg, #16a34a 0%, #22c55e 100%);
          color: white;
        }

        .btn-complete:hover:not(:disabled) {
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(22,163,74,0.3);
        }

        .btn-reopen {
          background: rgba(59,130,246,0.1);
          color: #3b82f6;
          border: 2px solid rgba(59,130,246,0.2);
        }

        .btn-reopen:hover:not(:disabled) {
          background: #3b82f6;
          color: white;
          transform: translateY(-2px);
          box-shadow: 0 8px 24px rgba(59,130,246,0.3);
        }

        .no-data {
          text-align: center;
          padding: 60px 24px;
          color: rgba(43,103,119,0.5);
          font-size: 16px;
          font-weight: 600;
        }

        @media (min-width: 1024px) {
          .sidebar {
            position: static;
            transform: translateX(0);
          }

          .close-btn {
            display: none;
          }

          .menu-toggle {
            display: none;
          }

          .overlay {
            display: none !important;
          }
        }

        @media (max-width: 1023px) {
          .close-btn {
            display: flex;
          }
        }

        @media (max-width: 768px) {
          .top-header {
            padding: 32px 16px;
          }

          .content-area {
            padding: 16px;
          }

          .page-header {
            padding: 32px 24px;
          }

          .page-title {
            font-size: 28px;
          }

          .stats-grid {
            grid-template-columns: 1fr;
          }

          .filter-section {
            flex-direction: column;
            align-items: stretch;
          }

          .search-box {
            min-width: 100%;
          }

          .request-header-row {
            flex-direction: column;
          }

          .request-right {
            flex-direction: row;
            width: 100%;
            align-items: center;
            justify-content: flex-start;
          }

          .modal-info-grid {
            grid-template-columns: 1fr;
          }

          .modal-footer {
            flex-direction: column;
          }

          .action-btn {
            width: 100%;
          }

          .notifications-panel {
            top: 90px;
            right: 16px;
            left: 16px;
            width: auto;
          }
        }
      `}</style>

      <div className="dashboard-container">
        {/* Sidebar */}
        <div className={`sidebar ${sidebarOpen ? 'open' : ''}`}>
          <div className="sidebar-header">
            <div className="sidebar-top">
              <button onClick={() => setSidebarOpen(false)} className="close-btn">
                ✕
              </button>
            </div>
            <div className="user-info-sidebar">
              <div className="user-avatar-sidebar">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
              <div className="user-details-sidebar">
                <div className="user-name-sidebar">{dashboardData?.adminName || 'Admin User'}</div>
                <div className="user-role-sidebar">{dashboardData?.hostelName || 'Hostel Administrator'}</div>
              </div>
            </div>
          </div>

          <div className="sidebar-nav">
            {/* Dynamic menu based on privileges */}
            {getPrivilegesByCategory('overview').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Overview</div>
                {getPrivilegesByCategory('overview').map((privilege) => (
                  <MenuItem key={privilege.page_id} icon={privilege.page_icon} label={privilege.page_name} route={privilege.page_route} />
                ))}
              </div>
            )}
            {getPrivilegesByCategory('management').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Management</div>
                {getPrivilegesByCategory('management').map((privilege) => (
                  <MenuItem key={privilege.page_id} icon={privilege.page_icon} label={privilege.page_name} active={privilege.page_route === 'hostel-admin-room'} route={privilege.page_route} />
                ))}
              </div>
            )}
            {getPrivilegesByCategory('approval').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Approval</div>
                {getPrivilegesByCategory('approval').map((privilege) => (
                  <MenuItem key={privilege.page_id} icon={privilege.page_icon} label={privilege.page_name} route={privilege.page_route} />
                ))}
              </div>
            )}
            {getPrivilegesByCategory('settings').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Settings</div>
                {getPrivilegesByCategory('settings').map((privilege) => (
                  <MenuItem key={privilege.page_id} icon={privilege.page_icon} label={privilege.page_name} route={privilege.page_route} />
                ))}
              </div>
            )}
            {getPrivilegesByCategory('reports').length > 0 && (
              <div className="nav-section">
                <div className="nav-label">Reports</div>
                {getPrivilegesByCategory('reports').map((privilege) => (
                  <MenuItem key={privilege.page_id} icon={privilege.page_icon} label={privilege.page_name} route={privilege.page_route} />
                ))}
              </div>
            )}
            {userPrivileges.length === 0 && !privilegesLoading && (
              <div className="no-privileges-message">
                <div className="no-privileges-icon">🔒</div>
                <div className="no-privileges-text">No Access Granted</div>
                <div className="no-privileges-subtext">Contact administrator</div>
              </div>
            )}
          </div>

          <div className="logout-section">
            <button onClick={handleLogout} className="logout-btn">
              🚪 Logout
            </button>
          </div>
        </div>

        {/* Overlays */}
        <div 
          className={`overlay ${sidebarOpen ? 'show' : ''}`}
          onClick={() => setSidebarOpen(false)}
        />
        <div
          className={`notification-overlay ${notificationOpen ? 'show' : ''}`}
          onClick={() => setNotificationOpen(false)}
        />

        {/* Main Content */}
        <div className="main-content">
          {/* Top Header */}
          <div className="top-header">
            <div className="header-left">
              <button onClick={() => setSidebarOpen(true)} className="menu-toggle">
                ☰
              </button>
              <div className="header-title-section">
                <div className="header-greeting">Room Management</div>
                <div className="header-name">{dashboardData?.hostelName || 'Admin Panel'}</div>
              </div>
            </div>
            <div className="header-right">
              <div className="user-avatar-header">
                {dashboardData?.adminName ? dashboardData.adminName.charAt(0).toUpperCase() : 'A'}
              </div>
            </div>
          </div>

          {/* Notifications Panel */}
          <div className={`notifications-panel ${notificationOpen ? 'open' : ''}`}>
            <div className="notifications-header">
              <div className="notifications-title">Notifications</div>
              <button 
                className="close-notification-btn"
                onClick={() => setNotificationOpen(false)}
              >
                ✕
              </button>
            </div>
            <div className="notifications-list">
              {notifications.map((notification) => (
                <div 
                  key={notification.id}
                  className="notification-item"
                >
                  <div className="notification-icon-wrapper">
                    {notification.icon}
                  </div>
                  <div className="notification-content">
                    <div className="notification-header-item">
                      <div className="notification-title">{notification.title}</div>
                    </div>
                    <div className="notification-message">{notification.message}</div>
                    <div className="notification-time">{notification.time}</div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Content Area */}
          <div className="content-area">
            {/* Page Header */}
            <div className="page-header">
              <h1 className="page-title">Room & Bed Management</h1>
              <p className="page-subtitle">🔴 Live room status with real-time student occupancy tracking</p>
            </div>

            {/* Statistics Bar */}
            {roomStats && (
              <div className="stats-bar">
                <div className="stat-card">
                  <div className="stat-value">{roomStats.total_rooms || 0}</div>
                  <div className="stat-label">Total Rooms</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{roomStats.total_occupied_beds || 0}/{roomStats.total_bed_capacity || 0}</div>
                  <div className="stat-label">Occupied Beds</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">{roomStats.single_rooms || 0}</div>
                  <div className="stat-label">Private Rooms</div>
                </div>
                <div className="stat-card">
                  <div className="stat-value">
                    {(roomStats.double_rooms || 0) + (roomStats.triple_rooms || 0) + (roomStats.dormitory_rooms || 0)}
                  </div>
                  <div className="stat-label">Shared Rooms</div>
                </div>
              </div>
            )}

            {/* Filter Section */}
            <div className="filter-section">
              <div className="search-box">
                <span className="search-icon">🔍</span>
                <input
                  type="text"
                  className="search-input"
                  placeholder="Search rooms, students, or phone numbers..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>
              <div className="filter-group">
                <span className="filter-label">Category:</span>
                <select 
                  className="filter-select"
                  value={roomCategoryFilter}
                  onChange={(e) => setRoomCategoryFilter(e.target.value)}
                >
                  <option value="all">All Categories</option>
                  <option value="single">Private Room</option>
                  <option value="double">2-Bed Sharing</option>
                  <option value="triple">3-Bed Sharing</option>
                  <option value="dormitory">4-Bed Sharing</option>
                </select>
              </div>
              <div className="filter-group">
                <span className="filter-label">Floor:</span>
                <select 
                  className="filter-select"
                  value={floorFilter}
                  onChange={(e) => setFloorFilter(e.target.value)}
                >
                  <option value="all">All Floors</option>
                  <option value="1">Ground Floor</option>
                  <option value="1">1st Floor</option>
                  <option value="2">2nd Floor</option>
                  <option value="3">3rd Floor</option>
                  <option value="4">4th Floor</option>
                </select>
              </div>
              <div className="filter-group">
                <span className="filter-label">Status:</span>
                <select 
                  className="filter-select"
                  value={availabilityFilter}
                  onChange={(e) => setAvailabilityFilter(e.target.value)}
                >
                  <option value="all">All Status</option>
                  <option value="available">Available</option>
                  <option value="not-available">Not Available</option>
                  <option value="partially-available">Partially</option>
                </select>
              </div>
            </div>

            {/* Rooms Grid */}
            {error ? (
              <div className="no-data">{error}</div>
            ) : filteredRooms.length > 0 ? (
              <div className="rooms-grid">
                {filteredRooms.map((room, index) => {
                  const availability = getAvailability(room);
                  const occupiedBeds = room.occupiedBeds || 0;
                  
                  return (
                    <div key={index} className="room-card">
                      <div className="room-header">
                        <div className="room-number">#{room.roomNumber}</div>
                        <div className="room-badges">
                          <div className="room-category-badge">{room.roomCategory || room.roomType}</div>
                          <div className={`ac-badge ${room.isAC ? 'ac' : 'non-ac'}`}>
                            {room.isAC ? '❄️ AC' : '🌡️ Non-AC'}
                          </div>
                        </div>
                      </div>

                      <div className="room-info">
                        <div className="room-info-item">
                          <span>🏢</span>
                          {room.floorNumber}th Floor
                        </div>
                        <div className="room-info-item">
                          <span>💰</span>
                          ₹{room.rentAmount?.toLocaleString()}/mo
                        </div>
                      </div>

                      <div className="room-status">
                        <div 
                          className="status-dot" 
                          style={{ color: getAvailabilityColor(availability) }}
                        />
                        <span 
                          className="status-text"
                          style={{ color: getAvailabilityColor(availability) }}
                        >
                          {availability} ({occupiedBeds}/{room.totalBeds} beds)
                        </span>
                      </div>

                      <div className="beds-section">
                        <div className="beds-header">🔴 Live Bed Status</div>
                        {room.beds.map((bed, bedIndex) => (
                          <div 
                            key={bedIndex} 
                            className={`bed-item ${bed.occupied ? 'occupied' : ''}`}
                          >
                            <div className="bed-info">
                              <div className="bed-icon">🛏️</div>
                              <div className="bed-details">
                                <div className="bed-number">Bed {bed.bedNumber}</div>
                                {bed.occupied && bed.student ? (
                                  <div className="bed-student occupied-by">
                                    <span>👤</span>
                                    <span className="bed-student-name">{bed.student.student_name}</span>
                                  </div>
                                ) : (
                                  <div className="bed-student">✅ Available</div>
                                )}
                              </div>
                            </div>
                            <div className="bed-actions">
                              {bed.occupied && bed.student ? (
                                <>
                                  <button 
                                    className="bed-btn info"
                                    title="View Student Details"
                                    onClick={() => viewStudentDetails(bed.student, room, bed.bedNumber)}
                                  >
                                    ℹ️
                                  </button>
                                  <button 
                                    className="bed-btn remove"
                                    onClick={() => handleRemoveStudent(
                                      room.roomNumber, 
                                      bed.bedNumber,
                                      bed.student.student_id
                                    )}
                                  >
                                    Remove
                                  </button>
                                </>
                              ) : (
                                <button 
                                  className="bed-btn assign"
                                  onClick={() => openAssignModal(room, bed.bedNumber)}
                                >
                                  Assign
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="no-data">No rooms found matching your filters</div>
            )}
          </div>
        </div>

        {/* Assign Student Modal */}
        {assignModalOpen && (
          <div className="modal-overlay" onClick={() => setAssignModalOpen(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">
                  Assign to Room {selectedRoom?.roomNumber} - Bed {selectedBed}
                </div>
                <button 
                  className="modal-close"
                  onClick={() => setAssignModalOpen(false)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-content">
                {availableStudents.length > 0 ? (
                  <>
                    {/* Unassigned Students Section */}
                    {availableStudents.filter(s => s.assignment_status === 'unassigned').length > 0 && (
                      <>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: 'bold', 
                          marginBottom: '10px',
                          color: '#f5576c',
                          borderBottom: '2px solid #f5576c',
                          paddingBottom: '5px'
                        }}>
                          🔲 Unassigned Students ({availableStudents.filter(s => s.assignment_status === 'unassigned').length})
                        </div>
                        <div className="student-list">
                          {availableStudents
                            .filter(s => s.assignment_status === 'unassigned')
                            .map((student) => (
                              <div key={student.application_id} className="student-item">
                                <div className="student-item-info">
                                  <div className="student-avatar">
                                    {student.full_name.charAt(0)}
                                  </div>
                                  <div className="student-details">
                                    <div className="student-name">{student.full_name}</div>
                                    <div className="student-contact">
                                      📞 {student.phone_number} • 📧 {student.email}
                                    </div>
                                  </div>
                                </div>
                                <button 
                                  className="assign-student-btn"
                                  onClick={() => handleAssignBed(student)}
                                  disabled={assignLoading}
                                >
                                  {assignLoading ? 'Assigning...' : 'Assign'}
                                </button>
                              </div>
                            ))}
                        </div>
                      </>
                    )}

                    {/* Already Assigned Students Section */}
                    {availableStudents.filter(s => s.assignment_status === 'assigned').length > 0 && (
                      <>
                        <div style={{ 
                          fontSize: '14px', 
                          fontWeight: 'bold', 
                          marginTop: '20px',
                          marginBottom: '10px',
                          color: '#4facfe',
                          borderBottom: '2px solid #4facfe',
                          paddingBottom: '5px'
                        }}>
                          ✅ Already Assigned ({availableStudents.filter(s => s.assignment_status === 'assigned').length})
                        </div>
                        <div className="student-list">
                          {availableStudents
                            .filter(s => s.assignment_status === 'assigned')
                            .map((student) => (
                              <div 
                                key={student.application_id} 
                                className="student-item"
                                style={{ opacity: '0.7', background: '#f0f9ff' }}
                              >
                                <div className="student-item-info">
                                  <div className="student-avatar" style={{ background: '#4facfe' }}>
                                    {student.full_name.charAt(0)}
                                  </div>
                                  <div className="student-details">
                                    <div className="student-name">{student.full_name}</div>
                                    <div className="student-contact">
                                      📞 {student.phone_number} • 📧 {student.email}
                                    </div>
                                    <div style={{ fontSize: '12px', color: '#0369a1', marginTop: '3px' }}>
                                      🏠 Room {student.assigned_room} - Bed {student.assigned_bed}
                                      {student.assigned_block && ` • Block ${student.assigned_block}`}
                                      {student.assigned_floor && ` • Floor ${student.assigned_floor}`}
                                    </div>
                                  </div>
                                </div>
                                <div style={{
                                  padding: '8px 15px',
                                  background: '#4facfe',
                                  color: 'white',
                                  borderRadius: '6px',
                                  fontSize: '12px',
                                  fontWeight: 'bold'
                                }}>
                                  ✅ Assigned
                                </div>
                              </div>
                            ))}
                        </div>
                      </>
                    )}
                  </>
                ) : (
                  <div className="no-data">No students found for this hostel</div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* Student Details Modal */}
        {studentDetailsModal && selectedStudent && (
          <div className="modal-overlay" onClick={() => setStudentDetailsModal(false)}>
            <div className="modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header">
                <div className="modal-title">👤 Student Details</div>
                <button 
                  className="modal-close"
                  onClick={() => setStudentDetailsModal(false)}
                >
                  ✕
                </button>
              </div>
              <div className="modal-content">
                <div className="student-details-card">
                  <div className="student-header-modal">
                    <div className="student-avatar-large">
                      {selectedStudent.student_name?.charAt(0) || 'S'}
                    </div>
                    <div className="student-info-modal">
                      <div className="student-name-large">{selectedStudent.student_name}</div>
                      <div className="student-meta">
                        <div className="meta-item">
                          <span>📞</span>
                          {selectedStudent.student_phone}
                        </div>
                        <div className="meta-item">
                          <span>📧</span>
                          {selectedStudent.student_email}
                        </div>
                      </div>
                    </div>
                  </div>

                  <div className="details-grid">
                    <div className="detail-card">
                      <div className="detail-label">Room Number</div>
                      <div className="detail-value">#{selectedStudent.roomNumber}</div>
                    </div>
                    <div className="detail-card">
                      <div className="detail-label">Bed Number</div>
                      <div className="detail-value">Bed {selectedStudent.bedNumber}</div>
                    </div>
                    <div className="detail-card">
                      <div className="detail-label">Floor</div>
                      <div className="detail-value">{selectedStudent.floorNumber}th Floor</div>
                    </div>
                    <div className="detail-card">
                      <div className="detail-label">Room Type</div>
                      <div className="detail-value">{selectedStudent.roomType}</div>
                    </div>
                    <div className="detail-card">
                      <div className="detail-label">AC Status</div>
                      <div className="detail-value">{selectedStudent.isAC ? '❄️ AC' : '🌡️ Non-AC'}</div>
                    </div>
                    <div className="detail-card">
                      <div className="detail-label">Monthly Rent</div>
                      <div className="detail-value">₹{selectedStudent.rentAmount?.toLocaleString()}</div>
                    </div>
                    {selectedStudent.join_date && (
                      <div className="detail-card">
                        <div className="detail-label">Joined On</div>
                        <div className="detail-value">
                          {new Date(selectedStudent.join_date).toLocaleDateString()}
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </>
  );
};

export default AdminRoomPage;